import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { Provider } from '../../../Models/Provider/Provider.model';
import IProviderSelectOption from '../../../Models/Provider/ProviderSelectOption.model';
import IRole from '../../../Models/Role/Role.model';
import { RootState } from '../../../reducers';
import { selectCurrentUsersCareTeam, setCareTeams, setExpandedRoleIndex, setSelectedTeam } from '../../../store/careTeams.slice';
import { setIsLoading } from '../../../store/shared.slice';
import { apiString } from '../../../utils/constants';
import Loading from '../../Shared/Loading';
import MyCareTeamDetails from './MyCareTeamDetails';
import MyTeamsSchedulesCalendar from '../../Calendar/MyTeamsSchedule/MyTeamsSchedulesCalendar';
import TeamWorkingHoursDisplay from '../TeamWorkingHoursDisplay';

import TeamScheduleNeedsSummaryView from './TeamScheduleNeedsSummary';
import { useWidgetContext } from '../../../hooks/widgetContext';

const MyTeamPage: React.FC = () => {
    const [providerOptions, setProviderOptions] = useState<IProviderSelectOption[]>([]);
    const [roles, setRoles] = useState<IRole[]>([]);
    const [teamTabIndex, setTeamTabIndex] = useState(0);
    const [detailsTabIndex, setDetailsTabIndex] = useState(0);
    const dispatch = useAppDispatch();
    const { careTeams, selectedTeam } = useSelector((state: RootState) => state.careTeams);
    const { setTeamMemberSelectedTeam } = useWidgetContext();

    useEffect(() => {
        dispatch(setCareTeams([]));
        const fetchProviderTeam = async () => {
            try {
                dispatch(selectCurrentUsersCareTeam());
            } catch (error) {
                console.error('Error fetching provider team:', error);
            }
        };

        const fetchProviderOptions = async () => {
            try {
                const response = await axios.get(`${apiString}/Provider/getactiveproviders`);
                if (response.status === 200) {
                    const responseOptions = response.data.map((provider: Provider) => ({
                        ProviderId: provider.Id,
                        ProviderName: `${provider.LastName}, ${provider.FirstName}`,
                        RoleName: provider.Role.RoleName,
                        RoleId: provider.Role.Id,
                    }));
                    setProviderOptions(responseOptions);
                }
            } catch (error) {
                console.error('Error fetching provider options:', error);
            }
        };

        const fetchRoles = async () => {
            try {
                const response = await axios.get(`${apiString}/role/roles`);
                if (response.status === 200) {
                    setRoles(response.data);
                }
            } catch (error) {
                console.error('Error fetching roles:', error);
            }
        };

        const fetchData = async () => {
            dispatch(setIsLoading(true));
            await Promise.all([fetchProviderTeam(), fetchProviderOptions(), fetchRoles()]);
            dispatch(setIsLoading(false));
        };

        fetchData();
    }, [dispatch]);
    return (
        <>
            <Loading message={'Loading team information...'} />
            <Grid container sx={{ height: '100vh', overflow: 'hidden' }} spacing={2}>
                <Grid size={12} sx={{ height: '100%' }}>
                    <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
                        {careTeams.length > 0 ? (
                            <>
                                <Tabs
                                    value={teamTabIndex}
                                    onChange={(e, newValue) => {
                                        setTeamTabIndex(newValue);
                                        const tabTeam = careTeams[newValue];
                                        dispatch(setSelectedTeam(tabTeam));
                                        dispatch(setExpandedRoleIndex(null)); // Collapse all expanded roles when switching teams
                                        if (tabTeam?.Id) {
                                            setTeamMemberSelectedTeam(tabTeam.Id);
                                        }
                                    }}
                                >
                                    {careTeams.map((team, index) => (
                                        <Tab key={index} label={team.Name} />
                                    ))}
                                </Tabs>
                                <Tabs value={detailsTabIndex} onChange={(e, newValue) => setDetailsTabIndex(newValue)}>
                                    <Tab label="Details" />
                                    <Tab label="Team Schedule" />
                                    <Tab label="Team Summary" />
                                </Tabs>
                                {detailsTabIndex === 0 && <MyCareTeamDetails roles={roles} providerOptions={providerOptions} />}
                                {detailsTabIndex === 1 && (
                                    <Box sx={{ p: 2 }}>
                                        {selectedTeam?.Id ? (
                                            <>
                                                <Box sx={{ mb: 3 }}>
                                                    <Typography variant="h6" sx={{ mb: 2 }}>
                                                        Team Working Hours
                                                    </Typography>
                                                    <TeamWorkingHoursDisplay workingHours={selectedTeam?.WorkingHours} />
                                                </Box>
                                                <Typography variant="h6" sx={{ mb: 2 }}>
                                                    Team Schedule
                                                </Typography>
                                                <MyTeamsSchedulesCalendar />
                                            </>
                                        ) : (
                                            <Typography>No team selected.</Typography>
                                        )}
                                    </Box>
                                )}
                                {detailsTabIndex === 2 && (
                                    <Box sx={{ p: 1 }}>
                                        <TeamScheduleNeedsSummaryView teamId={selectedTeam?.Id} />
                                    </Box>
                                )}
                            </>
                        ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                <Typography>No team information available.</Typography>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default MyTeamPage;
